import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import "./Hero.css";

// Fallback carousel images in case API fails
const fallbackImages = [
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80", // Mountain landscape
  "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80", // Nature scene
  "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=80", // Lake and mountains
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80", // Beach
  "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&q=80", // Travel destination
];

/**
 * Helper: decode HTML entities (e.g. &lt;strong&gt; -> <strong>)
 * Some admin UIs save escaped HTML, so decode first.
 */
const decodeHtmlEntities = (encodedStr) => {
  if (!encodedStr) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = encodedStr;
  return txt.value;
};

const Hero = () => {
  const [heroDescription, setHeroDescription] = useState("Loading...");
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // Carousel index
  const [introDescription, setIntroDescription] = useState("Loading...");
  const [anniversaryImage, setAnniversaryImage] = useState("");
  const [carouselImages, setCarouselImages] = useState(fallbackImages); // Dynamic carousel images

  // Fetch hero carousel images from API
  useEffect(() => {
    fetch("https://admin.newalliedtour.net/api/hero_images/")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          // Sort by order if needed and extract image URLs
          const sortedImages = data
            .sort((a, b) => a.order - b.order)
            .map((item) => item.image);
          setCarouselImages(sortedImages);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch hero images:", err);
        // Keep fallback images on error
      });
  }, []);

  // Carousel auto-rotation every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex + 1) % carouselImages.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // Fetch hero section from API
  useEffect(() => {
    fetch("https://admin.newalliedtour.net/api/hero_section/")
      .then((res) => res.json())
      .then((data) => {
        const raw = data.description || "TOGETHER WE GROW";
        // decode if admin sent escaped HTML
        const decoded = decodeHtmlEntities(raw);

        // sanitize and allow only these tags/attributes
        const clean = DOMPurify.sanitize(decoded, {
          ALLOWED_TAGS: ["b", "strong", "span", "em", "u"],
          ALLOWED_ATTR: ["style", "class"]
        });

        setHeroDescription(clean);
      })
      .catch(() => {
        setHeroDescription("TOGETHER WE GROW");
      });
  }, []);

  // Fetch anniversary section (description + image)
  useEffect(() => {
    fetch("https://admin.newalliedtour.net/api/anniversary_section/")
      .then((res) => res.json())
      .then((data) => {
        const anniversaryData = Array.isArray(data) ? data[0] : data;
        const rawIntro = anniversaryData.description || "";
        const decodedIntro = decodeHtmlEntities(rawIntro);
        const cleanIntro = DOMPurify.sanitize(decodedIntro, {
          ALLOWED_TAGS: ["b", "strong", "span", "em", "u"],
          ALLOWED_ATTR: ["style", "class"]
        });
        setIntroDescription(cleanIntro);
        setAnniversaryImage(anniversaryData.logo || "");
      })
      .catch(() => {
        setIntroDescription("");
        setAnniversaryImage("");
      });
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section
        className="siteBanner"
        style={{
          backgroundImage: `url(${carouselImages[currentImageIndex]})`
        }}
      >
        <div className="overlay"></div>
        <div className="blue-overlay"></div>
        <div className="hero-content">
          {/* Render sanitized HTML from admin. Poppins is applied in CSS already. */}
          <h1
            className="m-0 text-uppercase fw-lighter"
            data-aos="fade-up"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400 }}
            dangerouslySetInnerHTML={{ __html: heroDescription }}
          />
        </div>
      </section>

      {/* Intro / Anniversary Section */}
      <section className="siteSection siteIntro">
        <div className="container-fluid">
          <div className="row align-items-center d-flex gx-5">
            <div className="col-lg-4 col-12">
              {anniversaryImage && (
                <img
                  className="img-fluid hero-badge"
                  src={anniversaryImage}
                  alt="Anniversary Badge"
                  data-aos="zoom-out-right"
                  data-aos-anchor-placement="center-bottom"
                  data-aos-delay="200"
                  data-aos-easing="ease-out-back"
                />
              )}
            </div>
            <div className="col-lg-8 col-12">
              <p
                className="mb-3 text-white ps-xl-5 25years"
                dangerouslySetInnerHTML={{ __html: introDescription }}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
